import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/** Keeps a sliver visible so the meter reads as "listening" even in silence. */
const MIN_SCALE = 0.04;
/** Fast attack, slow release: speech peaks show at once, the bar settles calmly. */
const RELEASE = 0.85;

type Props = {
  /** Current input level 0–1; read once per animation frame. */
  getLevel: () => number;
  className?: string;
};

/**
 * Live microphone level. Drives the bar's transform directly per frame
 * (no React state), so recording never re-renders the composer at 60 fps.
 */
export function LevelMeter({ getLevel, className }: Props) {
  const barRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let frame = 0;
    let smoothed = 0;
    const tick = () => {
      const level = getLevel();
      smoothed = level > smoothed ? level : smoothed * RELEASE + level * (1 - RELEASE);
      if (barRef.current) barRef.current.style.transform = `scaleX(${Math.max(MIN_SCALE, smoothed)})`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [getLevel]);

  return (
    <span aria-hidden="true" className={cn('relative block h-1.5 overflow-hidden rounded-full bg-foreground/10', className)}>
      <span
        ref={barRef}
        className="absolute inset-0 origin-left rounded-full bg-brand will-change-transform"
        style={{ transform: `scaleX(${MIN_SCALE})` }}
      />
    </span>
  );
}

export default LevelMeter;
